// Client-side date display helpers. The SECURITY boundary (which weeks a tech may
// read) is enforced server-side in Eastern time; these local approximations are for
// display grouping only (archive list, picker filtering, "due" highlights).

/** ISO date (YYYY-MM-DD) for a local Date. */
export function localISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export const todayISO = (): string => localISO(new Date());

/** ISO Monday of the current local week. */
export function localMondayISO(): string {
  const now = new Date();
  const mon = new Date(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay() + 6) % 7));
  return localISO(mon);
}

/** "2m ago" / "3h ago" / "2d ago" from an epoch-ms or ISO timestamp. */
export function fmtAgo(t: number | string): string {
  const ms = typeof t === 'number' ? t : Date.parse(t);
  if (!ms || Number.isNaN(ms)) return '';
  const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/** Short display for an ISO date: 'Jun 22'. */
export function fmtShortDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const d = new Date(+m[1]!, +m[2]! - 1, +m[3]!);
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** Reminder chip text: 'Today' / 'Overdue · Jun 20' / 'Jun 29'. */
export function fmtReminderWhen(dateISO: string): string {
  const today = todayISO();
  if (!dateISO) return '';
  if (dateISO === today) return 'Today';
  if (dateISO < today) return `Overdue · ${fmtShortDate(dateISO)}`;
  return fmtShortDate(dateISO);
}

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri'] as const;
const DAY_NAMES: Record<string, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
};

/** Mon–Fri day stubs for a week id (ISO Monday): key + 'Monday, June 1' label. */
export function weekdayStubs(weekId: string): { key: string; label: string; date: string }[] {
  const m = weekId.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return DAY_KEYS.map((k) => ({ key: k, label: DAY_NAMES[k]!, date: '' }));
  return DAY_KEYS.map((k, i) => {
    const d = new Date(+m[1]!, +m[2]! - 1, +m[3]! + i);
    return {
      key: k,
      label: `${DAY_NAMES[k]}, ${d.toLocaleDateString([], { month: 'long', day: 'numeric' })}`,
      date: localISO(d),
    };
  });
}

/** Human device label from a user-agent string (People roster). */
export function deviceLabel(ua: string): string {
  if (!ua) return 'Unknown device';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Android/i.test(ua)) return 'Android';
  if (/Macintosh/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows PC';
  if (/Linux/i.test(ua)) return 'Linux';
  return 'Device';
}
